import ApplicationMaterials from './ApplicationMaterials';
import ApplicationAttachments from './ApplicationAttachments';
import { formatRating } from '../../utils/rating';
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import SquareFootRoundedIcon from "@mui/icons-material/SquareFootRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import DrawRoundedIcon from '@mui/icons-material/DrawRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import HailRoundedIcon from '@mui/icons-material/HailRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';

import { useContext, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Box, Button, Card, CardContent, Chip, Divider,
  Sheet, Slider, Stack, Typography, Select, Option,
  CircularProgress,
  Link, Modal, ModalDialog, ModalClose, Textarea
} from "@mui/joy";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";

import { APIClientPayApplication, APIClientEnsureApplicationChat, APIClientCheckPayment, APIClientGetApplication, APIClientRejectApplication, APIClientAcceptApplication, APIClientRateApplication } from "../../api";
import { LayoutContext } from "../../context";
import { formatCityAddress } from "../../shared/orderLocation";
import { getOrderCity } from "../../shared/orderLocation";
import { stripCityPrefix } from "../../shared/address";



// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CFG = {
  completed: { label: "Завершено", color: "success", icon: <CheckCircleRoundedIcon sx={{ fontSize: 14 }} /> },
  revision: { label: "Доработка", color: "danger", icon: <BuildRoundedIcon sx={{ fontSize: 14 }} /> },
  measuring_premises: { label: "Запланировано", color: "primary", icon: <EventAvailableRoundedIcon sx={{ fontSize: 14 }} /> },
  on_drawing: { label: "На черчении", color: "warning", icon: <DrawRoundedIcon sx={{ fontSize: 14 }} /> },
  new: { label: "Новое", color: "neutral", icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 14 }} /> },
  failed: { label: "Отменено", color: "danger", icon: <CancelRoundedIcon sx={{ fontSize: 14 }} /> },
};

// Хелпер для форматирования даты из ISO
function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("ru-RU", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Имя человека из объекта сотрудника. Сотрудник может прийти как вложенный
// объект, как усечённый объект без части полей, или отсутствовать — во всех
// случаях возвращаем строку, а не роняем карточку обращением к .name у не-объекта.
function personName(person) {
  if (!person || typeof person !== "object") return "";
  const parts = [person.name, person.last_name, person.patronymic].filter(Boolean);
  return parts.join(" ").trim();
}

// Берёт первые 5 символов времени («10:00:00» → «10:00») только если это строка.
// Время может прийти числом/пустым — тогда .slice упал бы и увёл карточку в ошибку.
function hhmm(value) {
  return typeof value === "string" && value.length >= 5 ? value.slice(0, 5) : null;
}

// Готовая строка «(интервал) дата» для выезда
function formatEventWhen(event) {
  if (!event || !event.date_event) return "";

  const exact = hhmm(event.time_event);
  const from = hhmm(event.time_from);
  const to = hhmm(event.time_to);

  let prefix = "";
  if (exact) prefix = `(${exact}) `;
  else if (from && to) prefix = `(${from}–${to}) `;
  else if (from) prefix = `(с ${from}) `;

  let dateStr = "";
  try {
    const d = new Date(event.date_event);
    if (!isNaN(d.getTime())) {
      dateStr = d.toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
    }
  } catch { /* оставим пустым */ }

  return `${prefix}${dateStr}`.trim();
}

// ─── Дедлайн для клиента: внутренний дедлайн + 24 часа ─────────────────────
// На бэке дедлайн приходит как ISO-строка. Подкорректируйте путь, если поле
// называется иначе (например, order.deadline или order.event.date_deadline).
function getClientDeadline(order) {
  const raw = order?.event?.deadline || order?.deadline || null;
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  // Прибавляем клиентский запас +24 часа от внутреннего дедлайна
  d.setHours(d.getHours() + 24);
  return d;
}

// Форматирование даты+времени дедлайна (без года, чтобы было компактнее)
function formatDeadline(date) {
  if (!date) return null;
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Состояние дедлайна для подсветки: 'overdue' | 'soon' | 'ok'
function getDeadlineState(date) {
  if (!date) return null;
  const diffMs = date.getTime() - Date.now();
  if (diffMs <= 0) return "overdue";
  if (diffMs <= 24 * 60 * 60 * 1000) return "soon"; // меньше суток
  return "ok";
}

const DEADLINE_CFG = {
  overdue: { color: "danger",  label: "Дедлайн (просрочен)" },
  soon:    { color: "warning", label: "Дедлайн (скоро)" },
  ok:      { color: "primary", label: "Дедлайн" },
};

// Заголовок секции в развёрнутом блоке
function SectionTitle({ children }) {
  return (
    <Typography
      level="body-xs"
      fontWeight={800}
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "primary.500",
        mb: 1,
        mt: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

// Одно поле с иконкой + лейблом + значением
function DetailField({ icon, label, value, full, link }) {
  if (!value) return null;
  return (
    <Box sx={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mb: 0.3, color: "text.tertiary" }}>
        {icon}
        <Typography level="body-xs" color="neutral" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {label}
        </Typography>
      </Stack>
      { link ?
        <Link href={link} level="body-sm" target="_blank" rel="noopener noreferrer" fontWeight={500} sx={{ wordBreak: "break-word" }}>
          {value}
        </Link>
      :
        <Typography level="body-sm" fontWeight={500} sx={{ wordBreak: "break-word" }}>
          {value}
        </Typography>
      }
    </Box>
  );
}





// ─── Ряд из 5 звёзд с подсветкой при наведении ────────────────────────────────
// value — текущая оценка (0..5). onChange(n) — выбор; повторный клик по той же
// звезде сбрасывает роль в 0. readOnly — только показать (для чипов не нужен).
function StarRating({ value = 0, onChange, readOnly = false, size = 30 }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= active;
        const Icon = filled ? StarRoundedIcon : StarBorderRoundedIcon;
        return (
          <Box
            key={n}
            component="span"
            role={readOnly ? undefined : "button"}
            aria-label={readOnly ? undefined : `Оценка ${n} из 5`}
            onMouseEnter={readOnly ? undefined : () => setHover(n)}
            onMouseLeave={readOnly ? undefined : () => setHover(0)}
            onClick={readOnly ? undefined : (e) => { e.stopPropagation(); onChange?.(value === n ? 0 : n); }}
            sx={{
              display: "inline-flex",
              cursor: readOnly ? "default" : "pointer",
              color: filled ? "warning.400" : "neutral.300",
              transition: "transform 0.12s ease, color 0.12s ease",
              "&:hover": readOnly ? {} : { transform: "scale(1.15)" },
            }}
          >
            <Icon sx={{ fontSize: size }} />
          </Box>
        );
      })}
    </Stack>
  );
}

// ─── OrderCard ────────────────────────────────────────────────────────────────
export default function OrderCard({ order: initialOrder, idx, onEdit, canEdit = true, isHidden = false, onRefresh, compact = false, detail = false }) {
  const { chatModal, setChatModal, chatUnread } = useContext(LayoutContext);

  const [expanded, setExpanded] = useState(detail);

  // Список приходит «тонким» (пагинация): в нём нет тяжёлых полей карточки
  // (event, payment, drawing_link, файл плана, файлы, программы). Догружаем полную
  // заявку деталью, когда карточка попадает во вьюпорт, и мёрджим поверх тонкой.
  const [order, setOrder] = useState(initialOrder);

  // Состояние чата — ОБЯЗАТЕЛЬНО после объявления order: useState(order.chat)
  // читает order, а до этой строки переменная в «мёртвой зоне» (TDZ) и
  // обращение к ней роняет всю карточку с ReferenceError.
  const [chatId, setChatId] = useState(order.chat || null);
  const [chatFixing, setChatFixing] = useState(false);
  const [chatError, setChatError] = useState('');
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { once: true, margin: "300px" });
  const detailLoadedRef = useRef(false);

  // Родитель прислал другую заявку (перезагрузка списка) — сбрасываем на неё
  // и разрешаем повторную догрузку детали.
  useEffect(() => {
    setOrder(initialOrder);
    detailLoadedRef.current = false;
  }, [initialOrder]);

  useEffect(() => { setChatId(order.chat || null); }, [order.chat]);

  const unreadValue = chatUnread?.ready
    ? (chatUnread.counts[String(chatId)] || 0) : (order.unread_count || 0);
  const unreadNumber = Number(unreadValue);
  const unreadCount = Number.isFinite(unreadNumber)
    ? Math.max(0, Math.floor(unreadNumber)) : 0;

  // Догрузка полной заявки при попадании во вьюпорт (один раз на заявку).
  // Тонкая заявка остаётся, если деталь не пришла (404 у дочернего аккаунта и т.п.).
  useEffect(() => {
    if (compact || !inView || detailLoadedRef.current) return;
    detailLoadedRef.current = true;
    let cancelled = false;
    (async () => {
      const full = await APIClientGetApplication(initialOrder.id);
      if (!cancelled && full && full.status !== 'error' && full.id) {
        setOrder(prev => ({ ...prev, ...full }));
      }
    })();
    return () => { cancelled = true; };
  }, [compact, inView, initialOrder.id, initialOrder]);

  const cfg = STATUS_CFG[order.status] || STATUS_CFG.new;

  // ── Оплата (эквайринг) ──
  const [payment, setPayment] = useState(order.payment || null);
  const [payLoading, setPayLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState("");

  const [acceptOpen, setAcceptOpen] = useState(false);
  const [decisionDeliveryId, setDecisionDeliveryId] = useState(null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptError, setAcceptError] = useState("");

  // ── Оценка заявки (рейтинг) ──
  const [rateOpen, setRateOpen] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState("");
  const [stars, setStars] = useState(0);   // единая оценка заявки 0..5
  const [rateComment, setRateComment] = useState("");

  // Когда деталь заявки догрузилась — подхватываем актуальный платёж
  useEffect(() => { setPayment(order.payment || null); }, [order.payment]);

  // Клиентский дедлайн (внутренний + 24ч)
  const clientDeadline = getClientDeadline(order);
  // Дедлайн показываем только для активных заявок — нет смысла на отменённых/завершённых
  const showDeadline = clientDeadline && !["completed", "failed"].includes(order.status);
  const deadlineState = showDeadline ? getDeadlineState(clientDeadline) : null;
  const deadlineCfg = deadlineState ? DEADLINE_CFG[deadlineState] : null;

  // Если на бэке есть status_display — используем его как label
  const statusLabel = order.status_display || cfg.label;


  // Есть ли блок «Контактное лицо»
  const hasContact = order.contact_name || order.contact_phone || order.contact_comment;
  // Есть ли блок «Доп. информация»
  const hasExtra = order.tz || order.comment || order.plan_file || order.input_files?.length > 0;
  
  
  const handlePay = async () => {
    if (payment?.payment_url) {
      window.open(payment.payment_url, "_blank", "noopener");
      return;
    }
    setPayLoading(true);
    const res = await APIClientPayApplication(order.id);
    setPayLoading(false);
    if (res?.payment) setPayment(res.payment);
    if (res?.payment?.payment_url) window.open(res.payment.payment_url, "_blank", "noopener");
  };

  const handleCheckPayment = async () => {
    const pid = payment?.id;
    if (!pid) { onRefresh?.(); return; }
    setCheckLoading(true);
    const res = await APIClientCheckPayment(pid);
    setCheckLoading(false);
    if (res?.id) {
      setPayment(res);
      if (res.is_paid) onRefresh?.(); // обновить заявку — появится итоговая ссылка
    }
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (reason.length < 5 || reason.length > 4000) {
      setRejectError('Опишите замечание: от 5 до 4000 символов'); return;
    }
    if (!decisionDeliveryId) { setRejectError('Обновите заявку: версия выдачи не найдена'); return; }
    setRejectLoading(true);
    setRejectError("");
    const res = await APIClientRejectApplication(order.id, decisionDeliveryId, reason);
    setRejectLoading(false);
    if (res?.status === 'ok') {
      setOrder(prev => ({
        ...prev,
        status: res.new_status || 'revision',
        status_display: res.status_display || 'Доработка',
        delivery: res.delivery || (prev.delivery ? { ...prev.delivery, can_accept: false, can_reject: false } : null),
      }));
      setRejectOpen(false);
      setRejectReason("");
      onRefresh?.();
    } else {
      setRejectError(res?.detail || 'Не удалось отправить на доработку');
    }
  };

  const handleAccept = async () => {
    if (!decisionDeliveryId) { setAcceptError('Обновите заявку: версия выдачи не найдена'); return; }
    setAcceptLoading(true);
    setAcceptError("");
    const res = await APIClientAcceptApplication(order.id, decisionDeliveryId);
    setAcceptLoading(false);
    if (res?.status === 'ok') {
      const canRate = res.can_rate ?? true;
      setOrder(prev => ({
        ...prev,
        is_accepted: true,
        can_client_accept: false,
        delivery: res.delivery || (prev.delivery ? { ...prev.delivery, decision: 'accepted', can_accept: false, can_reject: false } : null),
        can_rate: canRate,
        status: res.new_status || 'completed',
        status_display: res.status_display || prev.status_display,
      }));
      setAcceptOpen(false);
      // Заявка принята и ещё не оценена → сразу предлагаем поставить оценку.
      // ВАЖНО: список НЕ перезагружаем (onRefresh) — иначе карточка
      // пересоздаётся и модалка оценки тут же закрывается.
      if (canRate) {
        setStars(0);
        setRateComment("");
        setRateError("");
        setRateOpen(true);
      }
    } else {
      setAcceptError(res?.detail || 'Не удалось принять заявку');
    }
  };

  const openRate = (e) => {
    e?.stopPropagation?.();
    setRateError("");
    setRateOpen(true);
  };

  // Крестик / «Пропустить» — просто закрыть, эндпоинт НЕ вызываем.
  // Введённые звёзды/комментарий не сбрасываем: если клиент передумает и снова
  // откроет модалку по кнопке «Оценить заявку», его выбор сохранится.
  const closeRate = () => {
    if (rateLoading) return;
    setRateError("");
    setRateOpen(false);
  };

  const handleRate = async () => {
    setRateLoading(true);
    setRateError("");
    // Единая оценка по заявке: одно значение во все три поля,
    // чтобы не менять серверный контракт (звёзды 0..5).
    const res = await APIClientRateApplication(order.id, {
      surveyor_stars: stars,
      draftsman_stars: stars,
      manager_stars: stars,
      comment: rateComment.trim(),
    });
    setRateLoading(false);
    if (res?.status === 'ok') {
      // Список НЕ перезагружаем — карточку обновляем локально, чип появится сразу.
      setOrder(prev => ({ ...prev, rating: res.rating, can_rate: false }));
      setRateOpen(false);
    } else {
      setRateError(res?.detail || 'Не удалось сохранить оценку');
    }
  };

  const handleOpenChat = async () => {
    if (chatId) {
      setChatModal({ open: true, chatId, title: `Заявка #${order.bitrix_deal || order.id}`, applicationId: order.id });
      return;
    }

    // Чата нет — просим сервер создать его и сразу открываем
    setChatFixing(true);
    setChatError('');
    const res = await APIClientEnsureApplicationChat(order.id);
    setChatFixing(false);

    if (res?.status === 'ok' && res.chat_id) {
      setChatId(res.chat_id);
      setChatModal({ open: true, chatId: res.chat_id, title: `Заявка #${order.bitrix_deal || order.id}`, applicationId: order.id });
      return;
    }
    setChatError(res?.detail || 'Не удалось открыть чат. Напишите нам, пожалуйста, в мессенджер.');
  };

  return (
    <motion.div
      ref={cardRef}
      initial={detail ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: compact ? 0 : (idx || 0) * 0.07, duration: compact ? 0 : 0.38 }}
      onClick={compact ? (e) => e.stopPropagation() : undefined}
    >
      <Card
        variant="outlined"
        sx={{
          borderRadius: "16px",
          bgcolor: isHidden ? "neutral.50" : "background.surface",
          opacity: isHidden ? 0.62 : 1,
          filter: isHidden ? "grayscale(1)" : "none",
          // cursor: "pointer",
          transition: "box-shadow 0.2s, transform 0.2s",
          "&:hover": isHidden ? {} : { boxShadow: "0 8px 32px rgba(99,102,241,0.12)", transform: "translateY(-2px)" },
          overflow: "hidden",
          p: 0, gap: 0,
          ...(detail ? { border: 0, borderRadius: 0, bgcolor: "transparent", opacity: 1, filter: "none", "&:hover": {} } : {}),
          ...(compact ? { border: 0, borderRadius: 0, bgcolor: 'transparent', overflow: 'visible', '&:hover': {} } : {})
        }}
        // onClick={() => setExpanded(p => !p)}
      >
        {!compact && !detail && <Box sx={{ height: 3, background: `linear-gradient(90deg, var(--joy-palette-${cfg.color}-400), var(--joy-palette-${cfg.color}-200))` }} />}

        <CardContent sx={{ p: compact || detail ? 0 : 2.5 }}>
          {!compact && <>
          <Stack direction="column" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} sx={{ width: '100%' }} alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                <Sheet
                  variant="soft" color={cfg.color}
                  sx={{
                    width: 44, height: 44, borderRadius: "12px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AssignmentRoundedIcon />
                </Sheet>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    level="title-sm" fontWeight={700}
                    sx={{ whiteSpace: "wrap", maxWidth: { xs: 180, sm: 300 } }}
                  >
                    {formatCityAddress(order)}
                  </Typography>
                  <Typography level="body-xs" color="neutral" sx={{ mt: 0.3 }}>
                    {order.bitrix_deal ? `№${order.bitrix_deal}` : "№ присваивается…"}
                    {order.object_type && ` · ${order.object_type}`}
                    {order.square && ` · ${order.square} м²`}
                    {order.final_square ? ` · факт ${order.final_square} м²` : ""}
                  </Typography>
                </Box>
              </Stack>
              <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Chip variant="solid" color={cfg.color} size="sm" startDecorator={cfg.icon}>
                  {statusLabel}
                </Chip>
                {isHidden && (
                  <Chip variant="soft" color="neutral" size="sm">
                    Скрыта
                  </Chip>
                )}

                {order.price && (
                  <Typography level="body-sm" fontWeight={800}>
                    {order.price.toLocaleString()} ₽
                  </Typography>
                )}
              </Stack>
            </Stack>
          
            { order.event?.manager && (
              <Chip variant="outlined" color='neutral' size="sm" startDecorator={<ManageAccountsRoundedIcon/>}>
                {order.event?.manager && `Ваш менеджер: ${order.event?.manager?.name} ${order.event?.manager?.last_name || ""} ${order.event?.manager?.patronymic || ""}`}
              </Chip>
            )}
            
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1, width: '100%' }}>
              
              {/* Дедлайн (внутренний +24ч). Показываем для всех активных статусов */}
              {showDeadline && (
                <Sheet
                  variant="soft"
                  color={deadlineCfg.color}
                  sx={{ borderRadius: 15, p: 2, flex: 1, minWidth: 160 }}
                >
                  <DetailField
                    icon={<HourglassEmptyRoundedIcon sx={{ fontSize: 14 }} />}
                    label={deadlineCfg.label}
                    value={formatDeadline(clientDeadline)}
                    full
                  />
                </Sheet>
              )}

              {order?.event?.employee && (order.status === 'measuring_premises' && order?.event?.date_event) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1, width: '100%' }} flexWrap="wrap">
                  {/* Обмерщик — показываем если событие есть */}
                  {personName(order?.event?.employee) && (
                    <Sheet variant="outlined" color="neutral" sx={{ borderRadius: 15, p: 2, flex: 1, minWidth: 160, boxShadow: "0 8px 32px rgba(99,102,241,0.12)" }}>
                      <DetailField
                        icon={<HailRoundedIcon sx={{ fontSize: 14 }} />}
                        label="Ваш обмерщик"
                        value={personName(order.event.employee)}
                        full
                      />
                    </Sheet>
                  )}

                  {/* Дата события — если запланировано */}
                  {order.status === 'measuring_premises' && order?.event?.date_event && (
                    <Sheet variant="soft" color="primary" sx={{ borderRadius: 15, p: 2, flex: 1, minWidth: 160 }}>
                      <DetailField
                        icon={<CalendarMonthRoundedIcon sx={{ fontSize: 14 }} />}
                        label="Дата и время выезда"
                        value={formatEventWhen(order.event)}
                        full
                      />
                    </Sheet>
                  )}
                </Stack>
              )}

              {order.payment_required && (
                <Sheet variant="soft" color="warning" sx={{ borderRadius: 15, p: 2, width: '100%' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }}
                         justifyContent="space-between" spacing={1.5}>
                    <Box>
                      <Typography level="title-sm" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <PaymentRoundedIcon sx={{ fontSize: 18 }} /> Требуется оплатить
                      </Typography>
                      <Typography level="body-sm" color="neutral" sx={{ mt: 0.3 }}>
                        {payment?.amount
                          ? `К оплате: ${Number(payment.amount).toLocaleString('ru-RU')} ₽. Итоговый чертёж станет доступен после оплаты.`
                          : 'Итоговый чертёж станет доступен после оплаты.'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <Button size="sm" variant="solid" color="primary" loading={payLoading}
                              startDecorator={<PaymentRoundedIcon />} onClick={handlePay}
                              sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        Оплатить
                      </Button>
                      <Button size="sm" variant="soft" color="neutral" loading={checkLoading}
                              startDecorator={<SyncRoundedIcon />} onClick={handleCheckPayment}
                              sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        Проверить
                      </Button>
                    </Stack>
                  </Stack>
                </Sheet>
              )}

              <ApplicationMaterials order={order}/>

            </Stack>

          </Stack>

          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={detail ? false : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.28 }}
                style={{ overflow: "hidden" }}
              >
                <Divider sx={{ my: 1.5 }} />

                {/* ─── Объект ─── */}
                <SectionTitle>Объект</SectionTitle>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1.5, mb: 2 }}>
                  <DetailField
                    icon={<HomeWorkRoundedIcon sx={{ fontSize: 14 }} />}
                    label="Адрес"
                    value={stripCityPrefix(order.address, getOrderCity(order)) || order.address}
                    full
                  />
                  <DetailField
                    icon={<HomeWorkRoundedIcon sx={{ fontSize: 14 }} />}
                    label="Тип объекта"
                    value={order.object_type}
                  />
                  <DetailField
                    icon={<SquareFootRoundedIcon sx={{ fontSize: 14 }} />}
                    label="Площадь"
                    value={order.square ? `${order.square} м²` : null}
                  />
                  {order.final_square ? (
                    <DetailField
                      icon={<SquareFootRoundedIcon sx={{ fontSize: 14 }} />}
                      label="Итоговая площадь (замер)"
                      value={`${order.final_square} м²`}
                    />
                  ) : null}
                  <DetailField
                    icon={<DesignServicesRoundedIcon sx={{ fontSize: 14 }} />}
                    label="Программа"
                    value={order.program}
                  />
                  <DetailField
                    icon={<HomeWorkRoundedIcon sx={{ fontSize: 14 }} />}
                    label="Город"
                    value={order.city_name}
                  />
                </Box>

                {/* ─── Программы (массив) ─── */}
                {order.programs_display?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mb: 0.8, color: "text.tertiary" }}>
                      <DesignServicesRoundedIcon sx={{ fontSize: 14 }} />
                      <Typography level="body-xs" color="neutral" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Программы для отрисовки
                      </Typography>
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {order.programs_display.map(p => (
                        <Chip key={p.id} size="sm" variant="soft" color="primary">{p.name}</Chip>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* ─── Заказчик ─── */}
                <SectionTitle>Заказчик</SectionTitle>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1.5, mb: hasContact || hasExtra ? 2 : 0 }}>
                  <DetailField
                    icon={<PersonRoundedIcon sx={{ fontSize: 14 }} />}
                    label="Имя"
                    value={order.full_name}
                  />
                  <DetailField
                    icon={<PhoneRoundedIcon sx={{ fontSize: 14 }} />}
                    label="Телефон"
                    value={order.phone}
                  />
                </Box>

                {/* ─── Контактное лицо ─── */}
                {hasContact && (
                  <>
                    <SectionTitle>Контактное лицо на объекте</SectionTitle>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1.5, mb: hasExtra ? 2 : 0 }}>
                      <DetailField
                        icon={<PersonRoundedIcon sx={{ fontSize: 14 }} />}
                        label="Имя"
                        value={order.contact_name}
                      />
                      <DetailField
                        icon={<PhoneRoundedIcon sx={{ fontSize: 14 }} />}
                        label="Телефон"
                        value={order.contact_phone}
                      />
                      <DetailField
                        icon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                        label="Комментарий"
                        value={order.contact_comment}
                        full
                      />
                    </Box>
                  </>
                )}

                {/* ─── Доп. информация ─── */}
                {hasExtra && (
                  <>
                    <SectionTitle>Дополнительно</SectionTitle>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1.5 }}>
                      <DetailField
                        icon={<AssignmentRoundedIcon sx={{ fontSize: 14 }} />}
                        label="Техническое задание"
                        value={order.tz}
                        full
                      />
                      <DetailField
                        icon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                        label="Комментарий"
                        value={order.comment}
                        full
                      />
                    </Box>

                    <ApplicationAttachments order={order}/>

                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          </>}
          <Stack direction={"column"} justifyContent="space-between" alignItems={'stretch'} sx={{ mt: compact ? 0 : 1.5, gap: compact ? 0 : 2 }}>
            {!compact && <Box>
              <Typography level="body-xs" color="neutral">
                Создано {formatDate(order.date_create)}
              </Typography>
              {order.date_update && order.date_update !== order.date_create && (
                <Typography level="body-xs" color="neutral" sx={{ opacity: 0.7 }}>
                  Обновлено {formatDate(order.date_update)}
                </Typography>
              )}
            </Box>}
            <Stack direction={compact ? "row" : { xs: 'column', sm: "row" }} alignItems="center" justifyContent={compact ? "flex-start" : { xs: "center", sm: "flex-end" }} sx={{ gap: 1, flexWrap: "wrap", width: "100%" }}>
              <Stack sx={{ width: compact ? "auto" : { xs: "100%", sm: "auto" } }}>
                <Button
                  startDecorator={<ChatRoundedIcon />}
                  endDecorator={unreadCount > 0 ? (
                    <Box component="span" aria-hidden="true" sx={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      minWidth: 20, height: 20, px: 0.65, boxSizing: "border-box",
                      borderRadius: "999px", bgcolor: "danger.solidBg", color: "danger.solidColor",
                      fontSize: "11px", fontWeight: 700, lineHeight: 1, flexShrink: 0,
                      fontVariantNumeric: "tabular-nums",
                    }}>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </Box>
                  ) : null}
                  aria-label={`${compact ? "Чат" : "Открыть чат"}${unreadCount > 0 ? `, непрочитанных сообщений: ${unreadCount}` : ""}`}
                  title={unreadCount > 0 ? `Непрочитанных сообщений: ${unreadCount}` : "Открыть чат"}
                  variant="soft"
                  size="sm"
                  disabled={isHidden || chatFixing}
                  loading={chatFixing}
                  onClick={handleOpenChat}
                  sx={{ width: compact ? "auto" : { xs: "100%", sm: "auto" }, whiteSpace: "nowrap" }}
                >
                  {compact ? "Чат" : "Открыть чат"}
                </Button>

                {chatError && (
                  <Typography level="body-xs" color="danger" sx={{ mt: 0.5, maxWidth: 260 }}>
                    {chatError}
                  </Typography>
                )}
              </Stack>

              {order.delivery?.can_accept && !isHidden && (
                <Button
                  size="sm"
                  variant="solid"
                  color="success"
                  startDecorator={<CheckCircleRoundedIcon />}
                  onClick={(e) => { e.stopPropagation(); setDecisionDeliveryId(order.delivery.id); setAcceptError(""); setAcceptOpen(true); }}
                  sx={{ fontWeight: 600, width: compact ? "auto" : { xs: "100%", sm: "auto" } }}
                >
                  Принято
                </Button>
              )}

              {order.delivery?.can_reject && !isHidden && (
                <Button
                  size="sm"
                  variant="soft"
                  color="danger"
                  startDecorator={<CancelRoundedIcon />}
                  onClick={(e) => { e.stopPropagation(); setDecisionDeliveryId(order.delivery.id); setRejectError(""); setRejectOpen(true); }}
                  sx={{ fontWeight: 600, width: compact ? "auto" : { xs: "100%", sm: "auto" } }}
                >
                  Не принято
                </Button>
              )}

              {order.is_accepted && !isHidden && (
                <Chip size="sm" variant="soft" color="success" startDecorator={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />}
                      sx={{ fontWeight: 700 }}>
                  Принято
                </Chip>
              )}

              {order.can_rate && !isHidden && (
                <Button
                  size="sm"
                  variant="soft"
                  color="warning"
                  startDecorator={<StarRoundedIcon />}
                  onClick={openRate}
                  sx={{ fontWeight: 600, width: compact ? "auto" : { xs: "100%", sm: "auto" } }}
                >
                  Оценить заявку
                </Button>
              )}

              {order.status === "new" && canEdit && !isHidden && (
                <Button
                  size="sm"
                  variant="soft"
                  color="neutral"
                  startDecorator={<EditRoundedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.(order);
                  }}
                  sx={{ fontWeight: 600, width: compact ? "auto" : { xs: "100%", sm: "auto" } }}
                >
                  Редактировать
                </Button>
              )}
              
              <Stack 
                direction={'row'} 
                sx={{ 
                  cursor: "pointer", 
                  userSelect: "none", 
                  display: compact || detail ? "none" : "flex", 
                  alignItems: "center", gap: 0.5,
                  mt: { xs: 1, sm: 0 }, ml: { xs: 0, sm: 1 },
                }} 
                onClick={() => setExpanded(p => !p)}
              >
                <Typography level="body-xs" color="neutral">{expanded ? "Свернуть" : "Подробнее"}</Typography>
                {expanded
                  ? <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14, color: "text.tertiary" }} />
                  : <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14, color: "text.tertiary" }} />
                }
              </Stack>
              
            </Stack>
          </Stack>

          {/* Единая оценка заявки — чип внизу карточки */}
          {!compact && order.rating && !isHidden && (() => {
            const scores = [['Обмерщик', order.rating.surveyor_stars], ['Чертёжник', order.rating.draftsman_stars], ['Менеджер', order.rating.manager_stars]].filter(([, score]) => Number(score) > 0);
            if (!scores.length) return null;
            return (
              <Box sx={{ mt: 1.5 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Stack direction="row" alignItems="center" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography level="body-xs" color="neutral" fontWeight={700}
                              sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Ваша оценка
                  </Typography>
                  {scores.map(([role, score]) => <Chip key={role} size="sm" variant="soft" color="warning"
                        startDecorator={<StarRoundedIcon sx={{ fontSize: 14 }} />}
                        sx={{ fontWeight: 700 }}>
                    {role}: {formatRating(score)} / 5
                  </Chip>)}
                </Stack>
              </Box>
            );
          })()}

        </CardContent>
      </Card>

      {/* «Не принято» → доработка */}
      <Modal open={rejectOpen} onClose={() => { if (!rejectLoading) setRejectOpen(false); }}>
        <ModalDialog sx={{ maxWidth: 440, width: "100%", borderRadius: "14px" }}>
          <Typography level="title-md" startDecorator={<CancelRoundedIcon />} sx={{ fontWeight: 800, mb: 0.5 }}>
            Отправить на доработку
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", mb: 1.5 }}>
            Заявка №{order.bitrix_deal || order.id} будет помечена как «Не принято», менеджер получит уведомление. Опишите замечания: от 5 до 4000 символов.
          </Typography>
          <Textarea
            minRows={3}
            placeholder="Например: не совпадает площадь / есть замечания по чертежу"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ borderRadius: "10px", mb: 1 }}
          />
          {rejectError && <Typography level="body-sm" color="danger" sx={{ mb: 1 }}>{rejectError}</Typography>}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button variant="soft" color="neutral" onClick={() => setRejectOpen(false)} disabled={rejectLoading}
                    sx={{ borderRadius: "10px", flex: 1 }}>
              Отмена
            </Button>
            <Button variant="solid" color="danger" onClick={handleReject} loading={rejectLoading}
                    sx={{ borderRadius: "10px", flex: 2, fontWeight: 700 }}>
              Отправить на доработку
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* «Принято» → успешное завершение */}
      <Modal open={acceptOpen} onClose={() => { if (!acceptLoading) setAcceptOpen(false); }}>
        <ModalDialog sx={{ maxWidth: 440, width: "100%", borderRadius: "14px" }}>
          <Typography level="title-md" startDecorator={<CheckCircleRoundedIcon />} sx={{ fontWeight: 800, mb: 0.5 }}>
            Принять работу
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", mb: 1.5 }}>
            Заявка №{order.bitrix_deal || order.id} будет отмечена как «Принято» и успешно завершена. Подтвердить?
          </Typography>
          {acceptError && <Typography level="body-sm" color="danger" sx={{ mb: 1 }}>{acceptError}</Typography>}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button variant="soft" color="neutral" onClick={() => setAcceptOpen(false)} disabled={acceptLoading}
                    sx={{ borderRadius: "10px", flex: 1 }}>
              Отмена
            </Button>
            <Button variant="solid" color="success" onClick={handleAccept} loading={acceptLoading}
                    sx={{ borderRadius: "10px", flex: 2, fontWeight: 700 }}>
              Принять работу
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Модалка оценки заявки (рейтинг) */}
      <Modal open={rateOpen} onClose={closeRate}>
        <ModalDialog sx={{ maxWidth: 460, width: "100%", borderRadius: "16px" }}>
          <ModalClose disabled={rateLoading} />
          <Typography level="title-md"
                      startDecorator={<StarRoundedIcon sx={{ color: "warning.400" }} />}
                      sx={{ fontWeight: 800, mb: 0.5 }}>
            Оцените работу
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", mb: 1.5 }}>
            Заявка №{order.bitrix_deal || order.id}. Оцените работу по заявке в целом —
            это помогает нам стать лучше. Оценка необязательна.
          </Typography>

          <Sheet variant="soft" color="neutral"
                 sx={{ borderRadius: "12px", p: 2, display: "flex", flexDirection: "column",
                       alignItems: "center", gap: 1 }}>
            <Typography level="body-sm" fontWeight={700}>Ваша оценка</Typography>
            <StarRating value={stars} onChange={(v) => setStars(v)} size={38} />
          </Sheet>

          <Textarea
            minRows={3}
            placeholder="Комментарий (необязательно)"
            value={rateComment}
            onChange={(e) => setRateComment(e.target.value)}
            sx={{ borderRadius: "10px", mt: 1.5 }}
          />

          {rateError && (
            <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
              {rateError}
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="soft" color="neutral" onClick={closeRate} disabled={rateLoading}
                    sx={{ borderRadius: "10px", flex: 1 }}>
              Пропустить
            </Button>
            <Button variant="solid" color="warning" onClick={handleRate} loading={rateLoading}
                    disabled={stars === 0 && !rateComment.trim()}
                    sx={{ borderRadius: "10px", flex: 2, fontWeight: 700 }}>
              Отправить оценку
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </motion.div>
  );
}
